import React, { ChangeEvent, useEffect, useState } from "react";
import {
  ADD_CATEGORY,
  CREATE_TRANSFORMATION_FIELD,
  DATA_CONFIGURATION,
  TRANSFORMATION,
} from "../ISRConstants";
import { DeleteIcon, NavRightIcon } from "assests/GmsIcons";
import CategoryImport from "../Pages/DefineModel/Components/CategoryImport";
import {
  Data,
  TCategory,
  TConnector,
  TField,
  TProductCategory,
  TSubmodel,
  TTransformation,
  TIndexState, TUpsertISRProjectDataModelsInput,
} from "../Pages/DefineModel/model";
import {
  ENTER,
  MAX,
  MIN,
  COUNT,
  QUANTITY,
} from 'Pages/GMS/ISR/ISRConstants';
import { Link, useLocation } from "react-router-dom";
import ToggleButton from 'Components/Common/components/Inputs/ToggleButton';

type TCategoryConfigurations = {
  categoryTypeList: string[];
  categoryData: Data[];
  selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
  updateFieldDataConfiguration: (updatedConfig: TProductCategory) => void;
  setSubSelectedItem: React.Dispatch<
    React.SetStateAction<TTransformation | null>
  >;
  indexInfo: TIndexState;
  setIndex?: React.Dispatch<React.SetStateAction<TIndexState>>;
  userProjectDataModels: TUpsertISRProjectDataModelsInput;
  setUserProjectDataModels: React.Dispatch<React.SetStateAction<TUpsertISRProjectDataModelsInput>>;
};

const CategoryConfigurations = ({
  categoryTypeList,
  categoryData,
  selectedItem,
  updateFieldDataConfiguration,
  setSubSelectedItem,
  indexInfo,
  setIndex,
  userProjectDataModels,
  setUserProjectDataModels
}: TCategoryConfigurations) => {
  const location = useLocation();
  const [initialProductCategory, setInitialProductCategory] = useState<TProductCategory>({
    type: "",
    id: null,
    data: {
      compatioScore: false,
      type: "",
      name: "",
      minCount: 0, 
      maxCount: 1,
      minQuantity: 0, 
      maxQuantity: 1,
      belongsToDataModel: false,
      categories: { type: "", data: [] },
      transformations: [],
      required: false
    },
  });

  const isGuide = Boolean(location.pathname.includes('/guide/'));

  const [categoryConfiguration,setCategoryConfiguration,] = useState<TProductCategory | null>(initialProductCategory);
  const [selectedItems, setSelectedItems] = useState<TCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<{
    type: string;
    data: TCategory[];
  } | null>(null);
  const [selectedCategoriesMap, setSelectedCategoriesMap] = useState<{
    [key: string]: TCategory[];
  }>({});
  const allSelectedCategories: TCategory[] = Object.values(
    selectedCategoriesMap
  ).flat();
  const [showCategoryMenu, setShowCategoryMenu] = useState<boolean>(false);
  const openCategoryMenu = () => setShowCategoryMenu(true);

  useEffect(() => {
    const category = selectedItem as TProductCategory;
    if (category) {
      setCategoryConfiguration(category);
      setSelectedCategoriesMap({
        [category?.data?.categories?.type]: category?.data?.categories?.data,
      });
      setSelectedCategory(category?.data?.categories ?? { type: "", data: [] });
    } else {
      setCategoryConfiguration(initialProductCategory);
    }
  }, [initialProductCategory]);

  useEffect(() => {
    setInitialProductCategory(selectedItem as TProductCategory);
  }, [selectedItem]);


  const removeDataModelFieldMultiFilters = () => {
    setUserProjectDataModels((dataModels) => {
      const newDataModels = {...dataModels};
      const dataModelToUpdate = newDataModels.project.dataModels[indexInfo?.dataModelIndex ?? -1];
      if (dataModelToUpdate) {
        dataModelToUpdate.data.fields.forEach((e) => {
          if (e.dataConfiguration.multiFilter && (e.dataConfiguration.multiFilter.filters ?? []).length > 0) {
            e.dataConfiguration.multiFilter.filters = [];
          }
        });
      }
      return dataModels;
    });
  }

  const handleDeleteField = (index: number) => {
    removeDataModelFieldMultiFilters();
    const updatedCategories = [...allSelectedCategories];
    updatedCategories.splice(index, 1);

    setSelectedCategory((prevSelectedCategory) => {
      if (!prevSelectedCategory) return null;
      return {
        ...prevSelectedCategory,
        data: updatedCategories,
      };
    });

    setCategoryConfiguration((prevMap) => {
      if (!prevMap) return prevMap;
      const updatedData = {
        ...prevMap.data,
        categories: {
          type: selectedCategory?.type ?? "",
          data: updatedCategories,
        },
      };
      return {
        ...prevMap,
        data: updatedData,
      };
    });

    setSelectedCategoriesMap((prevMap) => ({
      ...prevMap,
      [selectedCategory?.type ?? ""]: updatedCategories,
    }));
  };

  const addNewTranformationField = () => {
    setCategoryConfiguration((prevMap) => {
      if (!prevMap) return prevMap;
      const newTranformation = { name: "", rules: [], type: TRANSFORMATION };
      const updatedData = {
        ...prevMap.data,
      };
      if (updatedData?.transformations) updatedData?.transformations?.push(newTranformation);
      else updatedData.transformations = [newTranformation];
      setTimeout(() => {
        if (setIndex)
          setIndex((prev) => ({
            ...prev,
            TransformationIndex: updatedData?.transformations?.length - 1,
          }));
        setSubSelectedItem(updatedData.transformations[updatedData?.transformations?.length - 1]);
      });
      const newState = {
        ...prevMap,
        data: updatedData,
      };
      return newState;
    });
  };

  useEffect(() => {
    if (allSelectedCategories.length === 0) return;
    if (categoryConfiguration) {
      updateFieldDataConfiguration(categoryConfiguration);
    }
  }, [categoryConfiguration]);

  const handleCompatioScore = (e:ChangeEvent<HTMLInputElement>)=>{
    const check = e?.target?.checked
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      prevConfig.data.compatioScore = check;
      return prevConfig;
    });
    if(categoryConfiguration) {
      updateFieldDataConfiguration(categoryConfiguration);
    }
  };

  const handlePredictiveSort = (e:ChangeEvent<HTMLInputElement>)=>{
    const check = e?.target?.checked
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      prevConfig.data.predictiveSort = check;
      return prevConfig;
    });
    if(categoryConfiguration) {
      updateFieldDataConfiguration(categoryConfiguration);
    }
  };

  const handleBuildToLaunch = (e:ChangeEvent<HTMLInputElement>)=>{
    const check = e?.target?.checked
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      prevConfig.data.buildToLaunch = check;
      return prevConfig;
    });
    if(categoryConfiguration) {
      updateFieldDataConfiguration(categoryConfiguration);
    }
  };

  const handleAutoAddToCart = (e:ChangeEvent<HTMLInputElement>)=>{
    const check = e?.target?.checked
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      prevConfig.data.autoAddToCart = check;
      return prevConfig;
    });
    if(categoryConfiguration) {
      updateFieldDataConfiguration(categoryConfiguration);
    }
  };

  const handleMinCount = (value: number) => {
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      const updatedData = {
        ...prevConfig.data,
        minCount: value,
      };
      return {
        ...prevConfig,
        data: updatedData,
      };
    });
  };
  
  const handleMaxCount = (value: number) => {
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      const updatedData = {
        ...prevConfig.data,
        maxCount: value,
      };
      return {
        ...prevConfig,
        data: updatedData,
      };
    });
  };
  
  const handleMinQuantity = (value: number) => {
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      const updatedData = {
        ...prevConfig.data,
        minQuantity: value,
      };
      return {
        ...prevConfig,
        data: updatedData,
      };
    });
  };
  
  const handleMaxQuantity = (value: number) => {
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      const updatedData = {
        ...prevConfig.data,
        maxQuantity: value,
      };
      return {
        ...prevConfig,
        data: updatedData,
      };
    });
  };

  return (
    <div className="cart-style-dm">
      <div className="tw-flex tw-justify-between tw-items-center ">
        <label className="cart-title">{DATA_CONFIGURATION}</label>
        <div className="tw-flex tw-flex-wrap tw-items-center">
          <button
            className={`btn-outlined ${
              categoryConfiguration?.data?.categories?.data?.length !== 0
                ? "tw-text-[#707275]"
                : "tw-text-primary"
            }`}
            disabled={
              categoryConfiguration?.data?.categories?.data?.length !== 0
            }
            onClick={openCategoryMenu}
          >
            { ADD_CATEGORY}
          </button>
          {categoryConfiguration?.data?.categories?.type ? (
            <button
              onClick={openCategoryMenu}
              className="tw-flex tw-items-center tw-p-0"
            >
              <label className="btn-outlined">
                {categoryConfiguration?.data?.categories?.type ?? ""}
              </label>
              <NavRightIcon />
            </button>
          ) : null}
        </div>
      </div>
      <div>
        {allSelectedCategories.map((category, index) => (
          <div key={category.id} className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-rounded tw-bg-shades-of-grey-0 tw-box-border tw-w-[95%] tw-flex tw-flex-row tw-items-center tw-justify-start tw-py-2 tw-px-2.5 tw-mt-1 tw-gap-[10px] tw-text-sm tw-text-slategray tw-border-[1px] tw-border-solid tw-border-whitesmoke-200">
              {/* <DragIcon /> */}
              <label className="tw-flex-1 tw-relative focus:tw-outline-none">
                {category?.name ?? ""}
              </label>
            </div>
            <button onClick={() => handleDeleteField(index)}>
              <DeleteIcon />
            </button>
          </div>
        ))}
      </div>
      <label className='cart-title tw-mt-5'>{COUNT}</label>
      <div className='tw-flex tw-items-center tw-mt-3'>
        <div className='tw-w-[20%] '>
          <label className='tw-text-sm tw-text-[#747593] tw-block'>{MIN}</label>
          <input
            className="input-field-dm" 
            type='number'
            value={categoryConfiguration?.data?.minCount ?? 0} 
            onChange={(e) => handleMinCount(Number(e.target.value))}
            placeholder={ENTER} />
        </div>
        <div className='tw-w-[20%] tw-ms-5'>
          <label className='tw-text-sm tw-text-[#747593] tw-block'>{MAX}</label>
          <input
            className="input-field-dm" 
            type='number'
            value={categoryConfiguration?.data?.maxCount ?? 0}
            onChange={(e) => handleMaxCount(Number(e.target.value))}
            placeholder={ENTER} />
        </div>
      </div>
      <label className='cart-title tw-mt-5'>{QUANTITY}</label>
      <div className='tw-flex tw-items-center tw-mt-3'>
        <div className='tw-w-[20%] '>
          <label className='tw-text-sm tw-text-[#747593] tw-block'>{MIN}</label>
          <input
            className="input-field-dm" 
            type='number'
            value={categoryConfiguration?.data?.minQuantity ?? 0} 
            onChange={(e) => handleMinQuantity(Number(e.target.value))}
            placeholder={ENTER} />
        </div>
        <div className='tw-w-[20%] tw-ms-5'>
          <label className='tw-text-sm tw-text-[#747593] tw-block'>{MAX}</label>
          <input
            className="input-field-dm"
            type='number'
            value={categoryConfiguration?.data?.maxQuantity ?? 0}
            onChange={(e) => handleMaxQuantity(Number(e.target.value))}
            placeholder={ENTER} />
        </div>
      </div>
      <div className="tw-flex tw-items-center tw-gap-16 tw-mt-6">
        <div >
          <label className='tw-text-sm tw-text-[#747593] tw-block tw-mb-2'>
            Auto add to build</label>
          <ToggleButton
            name="autoAddToCart"
            value={categoryConfiguration?.data?.autoAddToCart ?? false}
            onChange = {handleAutoAddToCart}
          />
        </div>
        <div >
          <label className='tw-text-sm tw-text-[#747593] tw-block tw-mb-2'>
            Import Repository Rules</label>
          <ToggleButton
            name="compatioScore" 
            value={categoryConfiguration?.data?.compatioScore ?? false}
            onChange = {handleCompatioScore} 
          />
        </div>
        {isGuide ? <div >
          <label className='tw-text-sm tw-text-[#747593] tw-block tw-mb-2'>
            Predictive sort </label>
          <ToggleButton
            name="predictiveSort" 
            value={categoryConfiguration?.data?.predictiveSort ?? false}
            onChange = {handlePredictiveSort} 
          />
        </div> : null}
        <div >
          <label className='tw-text-sm tw-text-[#747593] tw-block tw-mb-2'>Build to launch</label>
          <ToggleButton
            name="buildToLaunch"
            value={categoryConfiguration?.data?.buildToLaunch ?? true}
            onChange = {handleBuildToLaunch}
          />
        </div>
      </div>
      <Link
        to="#"
        onClick={() => {
          addNewTranformationField();
        }}
        className="tw-mt-5 tw-px-[22px] tw-py-2 tw-bg-white tw-rounded tw-border tw-border-blue-800 tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex"
        role="menuitem"
      >
        {CREATE_TRANSFORMATION_FIELD}
      </Link>

      {showCategoryMenu && (
        <CategoryImport
          showCategoryMenu={showCategoryMenu}
          setShowCategoryMenu={setShowCategoryMenu}
          categoryTypeList={categoryTypeList}
          categoryData={categoryData}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          setSelectedCategoriesMap={setSelectedCategoriesMap}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          setCategoryConfiguration={setCategoryConfiguration}
          removeDataModelFieldMultiFilters={removeDataModelFieldMultiFilters}
        />
      )}
    </div>
  );
};

export default CategoryConfigurations;
